import { Injectable } from '@angular/core';
import type { Oidc } from 'oidc-spa/core';
import { BehaviorSubject } from 'rxjs';
import { IAuthService } from '../interfaces/i-auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthOidcService implements IAuthService {

  isUserLogged = new BehaviorSubject<boolean>(false);

  private oidc!: Oidc;
  private _cachedToken: string | undefined;

  setup(oidc: Oidc): void {
    this.oidc = oidc;
    this.isUserLogged.next(oidc.isUserLoggedIn);

    if (oidc.isUserLoggedIn) {
      const loggedIn = oidc as Oidc.LoggedIn;
      loggedIn.getTokens().then(t => { this._cachedToken = t.accessToken; });
      loggedIn.subscribeToTokensChange(t => { this._cachedToken = t.accessToken; });
    }
  }

  getJwtToken(): string | undefined {
    return this._cachedToken;
  }

  async isLoggedIn(): Promise<boolean> {
    const logged = this.oidc?.isUserLoggedIn ?? false;
    this.isUserLogged.next(logged);
    return logged;
  }

  redirectToLoginPage(): void {
    if (this.oidc && !this.oidc.isUserLoggedIn) {
      (this.oidc as Oidc.NotLoggedIn).login({ doesCurrentHrefRequiresAuth: true });
    }
  }

  getUsername(): string | null {
    if (!this.oidc?.isUserLoggedIn) return null;
    const t = (this.oidc as Oidc.LoggedIn).getDecodedIdToken() as any;
    return t?.preferred_username ?? null;
  }

  getUserProfile(): any {
    if (!this.oidc?.isUserLoggedIn) return null;
    const t = (this.oidc as Oidc.LoggedIn).getDecodedIdToken() as any;
    return {
      username: t?.preferred_username,
      email: t?.email,
      firstName: t?.given_name,
      lastName: t?.family_name
    };
  }

  getProperty(prop: string): any {
    if (!this.oidc?.isUserLoggedIn) return undefined;
    const t = (this.oidc as Oidc.LoggedIn).getDecodedIdToken() as any;
    return t?.[prop] ?? undefined;
  }

  logout(): void {
    if (this.oidc?.isUserLoggedIn) {
      (this.oidc as Oidc.LoggedIn).logout({ redirectTo: 'home' });
    }
    this.isUserLogged.next(false);
    this._cachedToken = undefined;
  }

  hasRealmManagementRoles(): boolean {
    if (!this.oidc?.isUserLoggedIn) return false;
    const t = (this.oidc as Oidc.LoggedIn).getDecodedIdToken() as any;
    const roles = t?.resource_access?.['realm-management']?.roles;
    return Array.isArray(roles) && roles.length > 0;
  }

  getID() { throw new Error('Method not implemented.'); }
  envMessageToString() { throw new Error('Method not implemented.'); }
  resetPassword(_userId: string, _password: string) { throw new Error('Method not implemented.'); }
  tempPassword(_userId: string) { throw new Error('Method not implemented.'); }
  login(_username: string, _password: string): any { throw new Error('Method not implemented.'); }
}
